import { useEffect, useState } from "react"
import { useQuery } from "urql"

import type { Query, User } from "@/graphql/codegen/graphql"
import { UsersListDocument } from "@/graphql/codegen/graphql"

const LIMIT = 100

interface UseProductManagerProps {
  query?: string
  isAs400ProductManager?: boolean
  pause?: boolean
  userIds?: string[]
}

const useProductManager = ({
  query = "",
  isAs400ProductManager,
  userIds,
  pause = false,
}: UseProductManagerProps): {
  productManagerList: User[]
  fetching: boolean
  loadMore: () => void
  hasMore: boolean
} => {
  const [offset, setOffset] = useState(0)
  const [productManagerList, setProductManagerList] = useState<User[]>([])
  const [hasMore, setHasMore] = useState<boolean>(false)

  const [{ data, fetching }] = useQuery<Pick<Query, "user">>({
    query: UsersListDocument,
    variables: {
      input: {
        filter: { query, isAs400ProductManager, ...(userIds && userIds.length > 0 ? { userIds } : {}) },
        limit: LIMIT,
        offset,
      },
    },
    pause,
  })

  useEffect(() => {
    if (!data) return

    const productManagers = data?.user?.list?.__typename === "UserListSuccess" ? data?.user?.list?.users : []

    setProductManagerList((prev) => {
      const uniqueIds = new Set(prev.map((productManager) => productManager.userId))
      return offset === 0
        ? productManagers
        : [...prev, ...productManagers.filter((productManager) => !uniqueIds.has(productManager.userId))]
    })
    setHasMore(productManagers.length % LIMIT === 0)
  }, [data, offset])

  const loadMore = () => {
    setOffset((prevOffset) => prevOffset + LIMIT)
  }

  useEffect(() => {
    setOffset(0)
  }, [query])

  return {
    productManagerList,
    fetching,
    loadMore,
    hasMore,
  }
}

export default useProductManager
